import React, { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import Cookies from 'js-cookie';
import { socket } from '#GameFrontend/services/socket';
import moment from 'moment/moment';

interface UserState {
	username: string | null;
	lssk: string | null;
	validUntil: number | null;
	isAuthorized: boolean;
}

const initialState: UserState = {
	username: null,
	lssk: null,
	validUntil: null,
	isAuthorized: false,
};

type Action = { type: 'LOGIN'; payload: Partial<UserState> } | { type: 'LOGOUT' };

const UserContext = createContext<{
	state: UserState;
	dispatch: React.Dispatch<Action>;
	authReady: boolean;
}>({
	state: initialState,
	dispatch: () => {},
	authReady: false,
});

const userReducer = (state: UserState, action: Action): UserState => {
	switch (action.type) {
		case 'LOGIN':
			const newState = { ...state, ...action.payload, isAuthorized: true };
			if (newState.lssk !== null && newState.validUntil !== null) {
				Cookies.set(
					'userState',
					JSON.stringify({
						username: newState.username,
						lssk: newState.lssk,
						validUntil: newState.validUntil,
					}),
					{ expires: 7 }
				);
			}
			return newState;
		case 'LOGOUT':
			Cookies.remove('userState');
			return initialState;
		default:
			return state;
	}
};

const UserProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(userReducer, initialState);
	const [authReady, setAuthReady] = useState(false);

	useEffect(() => {
		let settled = false;
		const settle = () => {
			if (!settled) {
				settled = true;
				setAuthReady(true);
			}
		};

		const onLogin = (data: { success: boolean; data?: { username: string } }) => {
			if (data.success && data.data) {
				dispatch({ type: 'LOGIN', payload: { username: data.data.username } });
			} else {
				dispatch({ type: 'LOGOUT' });
			}
			settle();
		};

		const onLoginRemember = (data: {
			success: boolean;
			data?: { username: string; key: string; validUntil: number };
		}) => {
			if (data.success && data.data) {
				dispatch({
					type: 'LOGIN',
					payload: {
						username: data.data.username,
						lssk: data.data.key,
						validUntil: data.data.validUntil,
					},
				});
			} else {
				dispatch({ type: 'LOGOUT' });
			}
			settle();
		};

		socket.on('User:Login', onLogin);
		socket.on('User:Login:Remember', onLoginRemember);

		const cookieState = Cookies.get('userState');
		if (cookieState === undefined) {
			settle();
		} else {
			try {
				const { username, lssk, validUntil } = JSON.parse(cookieState);
				if (username && lssk && moment(validUntil).unix() >= moment().unix()) {
					socket.timeout(5000).emit('User:Login', { username, lssk }, () => settle());
				} else {
					Cookies.remove('userState');
					settle();
				}
			} catch {
				Cookies.remove('userState');
				settle();
			}
		}

		const fallback = window.setTimeout(settle, 6000);

		return () => {
			socket.off('User:Login', onLogin);
			socket.off('User:Login:Remember', onLoginRemember);
			window.clearTimeout(fallback);
		};
	}, []);

	return <UserContext.Provider value={{ state, dispatch, authReady }}>{children}</UserContext.Provider>;
};

export { UserContext, UserProvider, userReducer, initialState };
